import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { FileText, Send, Loader2, CheckCircle2, ArrowRight, MapPin, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";

const inputCls = "w-full rounded-sm bg-input border border-gold/20 px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-gold transition-shadow duration-200";
const labelCls = "block text-sm font-medium text-slate-300 mb-1.5";

const coverage = [
  "Halifax Regional Municipality", "Halifax Stanfield Airport", "South Shore (Lunenburg, Mahone Bay)",
  "Peggy's Cove", "Truro & Central NS", "Cape Breton (Sydney)", "New Brunswick & PEI", "Anywhere in the Maritimes",
];

export default function RequestQuote() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const [form, setForm] = useState(() => {
    const dest = params.get("destination");
    const svc = params.get("service");
    return {
      name: "", email: "", phone: "", service_type: svc || "", pickup_location: "",
      dropoff_location: dest || "", coverage_area: "", date: "", passengers: "",
      tour_duration: "", agreed_price: "",
      message: dest ? `I'd like a private tour to ${dest}.` : "",
    };
  });

  const setF = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || (!form.email && !form.phone)) return toast.error("Please give your name and a phone number or email so we can reply.");
    setLoading(true);
    try {
      const payload = { ...form };
      payload.agreed_price = form.agreed_price !== "" && !isNaN(parseFloat(form.agreed_price)) ? parseFloat(form.agreed_price) : null;
      const { data } = await api.post("/quote-requests", payload);
      trackEvent("quote_submitted", { service: form.service_type || "unspecified" });
      setDone(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not submit request.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <section className="max-w-2xl mx-auto px-5 sm:px-8 py-24">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="rounded-sm bg-navy-light border border-gold/30 p-10 text-center" data-testid="quote-request-done">
          <CheckCircle2 className="h-16 w-16 text-gold mx-auto mb-5" />
          <h1 className="font-heading text-3xl font-bold text-white">Request received!</h1>
          <p className="text-slate-300 mt-3">Thanks, {done.name}. Our team will review your request and email you a custom quote shortly.</p>
          <div className="mt-8 text-left rounded-sm bg-navy border border-gold/20 p-6 space-y-2 text-sm">
            {done.service_type && <div className="flex justify-between gap-4"><span className="text-slate-400">Service</span><span className="text-white text-right">{done.service_type}</span></div>}
            <div className="flex justify-between gap-4"><span className="text-slate-400">Pickup</span><span className="text-white text-right">{done.pickup_location}</span></div>
            {done.coverage_area && <div className="flex justify-between gap-4"><span className="text-slate-400">Coverage area</span><span className="text-white text-right">{done.coverage_area}</span></div>}
            <div className="flex justify-between gap-4"><span className="text-slate-400">Reference</span><span className="text-white">{done.id.slice(0, 8).toUpperCase()}</span></div>
          </div>
          <button onClick={() => navigate("/")} className="mt-8 inline-flex items-center gap-2 rounded-full bg-gold px-8 py-3.5 font-semibold text-navy transition-transform duration-200 hover:-translate-y-0.5 hover:bg-gold-light" data-testid="quote-done-home-btn">
            Back to home <ArrowRight className="h-4 w-4" />
          </button>
        </motion.div>
      </section>
    );
  }

  return (
    <div className="max-w-[1500px] mx-auto px-5 sm:px-10 pt-16 pb-24">
      <div className="mb-10 max-w-2xl">
        <span className="text-sm uppercase tracking-[0.2em] text-gold">Custom request</span>
        <h1 className="mt-3 font-heading text-4xl sm:text-5xl font-bold text-white tracking-tighter">Request a price quote</h1>
        <p className="mt-4 text-slate-300 leading-relaxed">
          Need something that isn't on our standard menu — a corporate account, group charter, multi-stop trip, or a
          destination we haven't listed? Tell us what you need and where, and we'll send you a tailored quote.
        </p>
      </div>

      <div className="grid gap-8 lg:grid-cols-3">
        <form onSubmit={submit} className="lg:col-span-2 rounded-sm bg-navy-light border border-gold/20 p-8" data-testid="quote-request-form">
          <div className="flex items-center gap-2 text-gold mb-6"><FileText className="h-5 w-5" /><h3 className="font-heading text-xl font-bold text-white">Tell us about your trip</h3></div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className={labelCls}>What service do you need?</label>
              <select data-testid="quote-service" value={form.service_type} onChange={setF("service_type")} className={inputCls}>
                <option value="">Select a service…</option>
                <option value="Tour">Tour</option>
                <option value="Nearby & Local Area Trip">Nearby &amp; Local Area Trip</option>
                <option value="Long Distance Trip">Long Distance Trip</option>
                <option value="Charter Touring Service">Charter Touring Service — Historic &amp; scenic site tours</option>
                <option value="Wedding Transportation">Wedding Transportation</option>
                <option value="Special Event">Special Event</option>
                <option value="Corporate Account">Corporate Account</option>
                <option value="Hourly Service">Hourly Service</option>
                <option value="Group Charter">Group Charter</option>
                <option value="Other">Other (describe below)</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Time of tour / duration <span className="text-slate-500">(optional)</span></label>
              <input data-testid="quote-tour-duration" value={form.tour_duration} onChange={setF("tour_duration")} placeholder="e.g. 3 hours, half-day, full day" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Agreed price <span className="text-slate-500">(optional)</span></label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gold">$</span>
                <input data-testid="quote-agreed-price" type="number" min="0" step="0.01" value={form.agreed_price} onChange={setF("agreed_price")} placeholder="If we've already agreed a price" className={`${inputCls} pl-7`} />
              </div>
            </div>
            <div>
              <label className={labelCls}>Pickup location</label>
              <input data-testid="quote-pickup" value={form.pickup_location} onChange={setF("pickup_location")} placeholder="City / address / airport" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Destination <span className="text-slate-500">(optional)</span></label>
              <input data-testid="quote-dropoff" value={form.dropoff_location} onChange={setF("dropoff_location")} placeholder="Where to?" className={inputCls} />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Service coverage area</label>
              <input data-testid="quote-coverage" value={form.coverage_area} onChange={setF("coverage_area")} placeholder="Which region(s) does your trip cover?" className={inputCls} list="coverage-list" />
              <datalist id="coverage-list">{coverage.map((c) => <option key={c} value={c} />)}</datalist>
            </div>
            <div>
              <label className={labelCls}>Preferred date <span className="text-slate-500">(optional)</span></label>
              <input data-testid="quote-date" value={form.date} onChange={setF("date")} placeholder="e.g. Aug 12, 2026 or flexible" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Passengers <span className="text-slate-500">(optional)</span></label>
              <input data-testid="quote-passengers" value={form.passengers} onChange={setF("passengers")} placeholder="e.g. 8 people" className={inputCls} />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Details <span className="text-slate-500">(optional)</span></label>
              <textarea data-testid="quote-message" rows={4} value={form.message} onChange={setF("message")} placeholder="Anything else we should know? Timing, stops, luggage, occasion…" className={`${inputCls} resize-none`} />
            </div>
          </div>

          <div className="mt-6 border-t border-gold/15 pt-6">
            <div className="flex items-center gap-2 text-gold mb-4"><FileText className="h-5 w-5" /><h4 className="font-heading text-lg font-bold text-white">Your contact details</h4></div>
            <div className="grid gap-4 sm:grid-cols-2">
              <input data-testid="quote-name" value={form.name} onChange={setF("name")} placeholder="Full name" className={inputCls} />
              <input data-testid="quote-phone" value={form.phone} onChange={setF("phone")} placeholder="Phone number" className={inputCls} />
              <input data-testid="quote-email" type="email" value={form.email} onChange={setF("email")} placeholder="Email address" className={`${inputCls} sm:col-span-2`} />
            </div>
          </div>

          <button type="submit" disabled={loading} data-testid="quote-submit"
            className="mt-7 inline-flex items-center justify-center gap-2 rounded-full bg-gold px-8 py-4 font-semibold text-navy transition-transform duration-200 hover:-translate-y-0.5 hover:bg-gold-light disabled:opacity-60">
            {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : <>Request my quote <Send className="h-4 w-4" /></>}
          </button>
        </form>

        <aside className="lg:col-span-1 space-y-6">
          <div className="rounded-sm bg-navy-light border border-gold/20 p-7">
            <div className="flex items-center gap-2 text-gold mb-4"><MapPin className="h-5 w-5" /><h3 className="font-heading text-lg font-bold text-white">Where we cover</h3></div>
            <p className="text-slate-400 text-sm mb-4">Based in Halifax, we serve all of Nova Scotia and the wider Maritimes:</p>
            <ul className="space-y-2.5">
              {coverage.map((c) => (
                <li key={c} className="flex items-center gap-2 text-slate-300 text-sm"><span className="h-1.5 w-1.5 rounded-full bg-gold shrink-0" /> {c}</li>
              ))}
            </ul>
          </div>
          <div className="rounded-sm bg-gradient-to-br from-gold to-gold-light p-7 text-navy">
            <ShieldCheck className="h-8 w-8 mb-3" />
            <h3 className="font-heading text-lg font-bold">No obligation</h3>
            <p className="text-sm text-navy/80 mt-1">Requesting a quote is completely free. We'll reply with clear, upfront pricing — no hidden fees.</p>
          </div>
        </aside>
      </div>
    </div>
  );
}
